import { computed } from '@angular/core';
import {
    signalStore,
    withState,
    withComputed,
    withMethods,
    patchState,
} from '@ngrx/signals';
import { FolderData as BaseFolderData } from '@app/shared/models/store-types';
import { NewDeliverNoteBundleProgress } from '@app/shared/models/business-model/delivery-note-bundles';

// Re-export the type for backward compatibility
export type FolderData = BaseFolderData;

export interface FoldersState {
    folders: FolderData[];
    loading: boolean;
    error: string | null;
    lastUpdated: number;
    // UI progress for the new-bundle creation wizard (bundle = folder domain).
    newBundleProgressButton: NewDeliverNoteBundleProgress;
}

const initialState: FoldersState = {
    folders: [],
    loading: false,
    error: null,
    lastUpdated: 0,
    newBundleProgressButton: { currentTab: 0, progess: 0 },
};

export const FoldersStore = signalStore(
    { providedIn: 'root' },
    withState<FoldersState>(initialState),
    withComputed(({ folders }) => ({
        foldersCount: computed(() => folders().length),
    })),
    withMethods(store => ({
        setFoldersLoading(loading: boolean): void {
            patchState(store, { loading });
        },
        setFoldersError(error: string | null): void {
            patchState(store, { error });
        },
        setFolders(folders: FolderData[]): void {
            patchState(store, { folders, lastUpdated: Date.now() });
        },
        addFolder(folder: FolderData): void {
            patchState(store, state => ({
                folders: [...state.folders, folder],
                lastUpdated: Date.now(),
            }));
        },
        updateFolder(updatedFolder: FolderData): void {
            patchState(store, state => ({
                folders: state.folders.map(folder =>
                    folder._key === updatedFolder._key
                        ? updatedFolder
                        : folder
                ),
                lastUpdated: Date.now(),
            }));
        },
        removeFolder(folderKey: string): void {
            patchState(store, state => ({
                folders: state.folders.filter(
                    folder => folder._key !== folderKey
                ),
                lastUpdated: Date.now(),
            }));
        },
        clearFolders(): void {
            patchState(store, { folders: [], lastUpdated: Date.now() });
        },
        setNewBundleProgressButton(
            progress: NewDeliverNoteBundleProgress
        ): void {
            patchState(store, { newBundleProgressButton: progress });
        },
    }))
);
